import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG =
  "https://images.pexels.com/photos/30475898/pexels-photo-30475898.jpeg?auto=compress&cs=tinysrgb&w=2400";

/** Custom easing shared with the design guidelines. */
const REVEAL_EASE = [0.76, 0, 0.24, 1];

const lines = [
  ["A", "US-Based"],
  ["Global", "Partner."],
  ["Industrial Supply", "& Trade."],
  ["Logistics."],
  ["Engineering."],
];

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="mask-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, ease: REVEAL_EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="hero"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -top-[10%] h-[120%] w-full"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover img-graded"
          draggable="false"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-b from-navy-base via-navy-base/70 to-navy-base"
      />
      {/* Radial gold spotlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 40% at 15% 60%, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0) 70%)",
        }}
      />

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-screen flex flex-col justify-between"
      >
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-4"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="overline">MEND · Middle East New Direction Solutions</span>
          </motion.div>

          <h1 className="h-display mt-10 text-[46px] leading-[1.02] sm:text-[68px] md:text-[92px] lg:text-[112px] font-medium">
            {lines.map((words, i) => (
              <MaskLine key={i} delay={0.35 + i * 0.11}>
                {words.map((w, j) => (
                  <span key={j} className={j === words.length - 1 ? "text-gold" : ""}>
                    {w}
                    {j < words.length - 1 ? " " : ""}
                  </span>
                ))}
              </MaskLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-12 max-w-xl font-sans text-white/70 text-[15px] md:text-[16px] leading-[1.75]"
          >
            An American company built on Middle East fluency. We protect Americans
            operating in Iraq, run life-support and logistics for demanding theatres,
            and deliver industrial supply and engineering execution alongside our
            partners in Jordan and Beijing.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <a href="#services" className="btn-gold" data-testid="hero-cta-services">
              Explore Capabilities
              <span aria-hidden>→</span>
            </a>
            <a href="#contact" className="btn-ghost" data-testid="hero-cta-contact">
              Request Brief
            </a>
          </motion.div>
        </div>

        {/* Bottom rail */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-16 flex flex-col md:flex-row md:items-end justify-between gap-8 border-t border-navy-border pt-8"
        >
          <div className="flex flex-wrap gap-x-16 gap-y-6">
            {[
              { k: "HQ", v: "United States" },
              { k: "Partners", v: "Jordan · Beijing" },
              { k: "Operations", v: "Iraq · MENA · Africa" },
            ].map((s) => (
              <div key={s.k} className="flex flex-col">
                <span className="overline">{s.k}</span>
                <span className="font-display text-2xl mt-2">{s.v}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-white/60">
            <span>Scroll</span>
            <span className="inline-block h-px w-12 bg-white/40 relative overflow-hidden">
              <span
                className="absolute inset-y-0 left-0 w-1/3 bg-gold animate-[scrollhint_2.2s_ease-in-out_infinite]"
                style={{
                  animation: "scrollhint 2.2s ease-in-out infinite",
                }}
              />
            </span>
          </div>
        </motion.div>
      </motion.div>

      <style>{`
        @keyframes scrollhint {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(200%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </section>
  );
}
